import React, { useLayoutEffect, useRef } from 'react';
import { useBudgets } from 'src/hooks/queries/budgets';
import BudgetService from 'src/services/budget.service';
import SummaryStat from 'src/components/atomic/molecules/summaryStat/SummaryStat';
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';
import { useCategories } from 'src/hooks/queries/categories';
import Selectors from 'src/utils/selectors';
import styles from './BudgetStatsBox.module.scss';
import statsBoxStyles from 'src/components/atomic/organisms/statsBox/statsBox.module.scss';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import adobeTags from 'src/types/adobe-tags';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { useIsFetching } from '@tanstack/react-query';
import colors from 'src/styles/colors.module.scss';
import StatsBox from 'src/components/atomic/organisms/statsBox/StatsBox';

export default function BudgetStatsBox(): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const titleRef = useRef<HTMLSpanElement>();
    const { isLoading: isLoadingBudgets, data: budgets } = useBudgets();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const showBudgetStatsBox: boolean = BudgetService.getCategoryBudgets(budgets)?.length > 0;
    const isLoading = isLoadingBudgets || isLoadingCategories;
    const isFetching = useIsFetching();

    useLayoutEffect(() => {
        if (isFetching === 0 && showBudgetStatsBox && searchParams.get('savedBudgets') && titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: 'smooth' });
            setSearchParams((prev) => {
                prev.delete('savedBudgets');
                return prev;
            });
        }
    }, [showBudgetStatsBox, isFetching]);

    if (isLoading || !showBudgetStatsBox) return <></>;

    const budgetForTrackedCategories: number = BudgetService.getAllParentCategoriesTotal(budgets);
    const totalSpent: number = BudgetService.currentlySpentCategoryTotal(budgets, categories);
    const leftOver: number = budgetForTrackedCategories - totalSpent > 0 ? budgetForTrackedCategories - totalSpent : 0;

    return (
        <div className={styles.container} data-cy={Selectors.SpendSmart.BUDGET_STATS_BOX}>
            <span ref={titleRef}>
                <Typography variant={TypographyVariants.HEADING_S} component="h2" className={colors.brandStrong}>
                    {LiteralsService.getLiteral('Budget.Current.budgetStatsBox.title')}
                </Typography>
            </span>
            <StatsBox>
                <SummaryStat
                    title={LiteralsService.getLiteral('Budget.Current.budgetStatsBox.budgetForTrackedCategoriesTitle')}
                    tooltip={LiteralsService.getLiteral(
                        'Budget.Current.budgetStatsBox.budgetForTrackedCategoriesTooltip'
                    )}
                    amount={FormatService.formatCurrency(budgetForTrackedCategories, { round: true })}
                    tagName={adobeTags.Tooltip.BudgetStatsBox.Budget}
                />
                <SummaryStat
                    title={LiteralsService.getLiteral('Budget.Current.budgetStatsBox.totalSpentBudgetCategoriesTitle')}
                    tooltip={LiteralsService.getLiteral(
                        'Budget.Current.budgetStatsBox.totalSpentBudgetCategoriesTooltip'
                    )}
                    amount={FormatService.formatCurrency(totalSpent)}
                    tagName={adobeTags.Tooltip.BudgetStatsBox.TotalSpent}
                />
                <SummaryStat
                    title={LiteralsService.getLiteral('Budget.Current.budgetStatsBox.leftOverBudgetCategoriesTitle')}
                    tooltip={LiteralsService.getLiteral(
                        'Budget.Current.budgetStatsBox.leftOverBudgetCategoriesTooltip'
                    )}
                    amount={FormatService.formatCurrency(leftOver)}
                    tooltipClassName={statsBoxStyles.thirdTooltip}
                    isGreen={leftOver > 0}
                    tagName={adobeTags.Tooltip.BudgetStatsBox.LeftOver}
                />
            </StatsBox>
        </div>
    );
}
